import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Minus, Plus, ArrowLeft, Check } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetailPage = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  
  const { addToCart } = useCart();
  const { isAuthenticated, isAdmin, isDriver } = useAuth();

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  useEffect(() => {
    if (product) {
      document.title = `${product.name} - R${product.selling_price.toFixed(2)} | NarkOnline`;
    }
  }, [product]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Failed to fetch product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (isAdmin || isDriver) {
      toast.error('Admins and drivers cannot add items to cart');
      return;
    }

    try {
      setAdding(true);
      await addToCart(product.id, quantity, product);
      setAdded(true);
      toast.success(`${quantity} x ${product.name} added to cart`);
      setTimeout(() => setAdded(false), 2000);
    } catch (error) {
      toast.error('Failed to add to cart');
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-stone-900 mb-2">Product not found</h2>
            <Link to="/products" className="text-green-900 hover:underline">
              Browse products
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-stone-50" data-testid="product-detail-page">
      <Navbar />
      
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Breadcrumb */}
          <Link to="/products" className="inline-flex items-center text-stone-600 hover:text-green-900 mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Products
          </Link>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Image */}
            <div className="aspect-square rounded-2xl overflow-hidden bg-white shadow-sm">
              {product.image_url ? (
                <img 
                  src={product.image_url} 
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-stone-400 bg-stone-100">
                  No Image Available
                </div>
              )}
            </div>

            {/* Details */}
            <div className="space-y-6">
              <div>
                <span className="text-sm text-stone-500 uppercase tracking-wider">{product.unit}</span>
                <h1 className="heading-section text-stone-900 mt-1" data-testid="product-title">
                  {product.name}
                </h1>
              </div>

              {/* Stock Status */}
              {product.in_stock ? (
                <span className="badge-success" data-testid="stock-status">In Stock</span>
              ) : (
                <span className="badge-error" data-testid="stock-status">Out of Stock</span>
              )}

              {/* Price */}
              <div className="space-y-1">
                <p className="text-3xl font-bold text-green-900" data-testid="product-price">
                  R{product.selling_price.toFixed(2)}
                </p>
                <p className="text-sm text-stone-500">
                  Cost: R{product.cost_price.toFixed(2)} (+10% service fee)
                </p>
              </div>

              {/* Description */}
              {product.description && (
                <div>
                  <h3 className="font-semibold text-stone-900 mb-2">Description</h3>
                  <p className="text-stone-600">{product.description}</p>
                </div>
              )}

              {/* SKU */}
              <p className="text-sm text-stone-500">
                SKU: <span className="font-mono">{product.sku}</span>
              </p>

              {/* Add to Cart */}
              {!isAdmin && !isDriver && (
                <div className="space-y-4 pt-4 border-t border-stone-200">
                  <div className="flex items-center gap-4">
                    <span className="font-medium text-stone-900">Quantity:</span>
                    <div className="flex items-center gap-2">
                      <button
                        className="qty-btn"
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        disabled={quantity <= 1}
                        data-testid="qty-decrease"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-12 text-center font-semibold" data-testid="qty-value">
                        {quantity}
                      </span>
                      <button
                        className="qty-btn"
                        onClick={() => setQuantity(quantity + 1)}
                        data-testid="qty-increase"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <Button
                    className={`w-full py-6 text-lg rounded-full transition-all ${
                      added 
                        ? 'bg-green-600 hover:bg-green-600' 
                        : 'bg-green-900 hover:bg-green-800'
                    }`}
                    onClick={handleAddToCart}
                    disabled={adding || !product.in_stock}
                    data-testid="add-to-cart-btn"
                  >
                    {added ? (
                      <>
                        <Check className="w-5 h-5 mr-2" />
                        Added to Cart
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-5 h-5 mr-2" />
                        {product.in_stock ? 'Add to Cart' : 'Out of Stock'}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
